import AppButton from '@nxsys/components/AppButton';
import {
  defaultHolidayPayCalcOptions,
  fromLeaveYearStart,
  holidayPaymentMethodOptions,
  leaveYearStartDateOptions,
  toLeaveYearStart,
} from '@nxsys/constants/AppConstants';
import { YesNoNAEnum } from '@nxsys/constants/AppEnums';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { UpdateCompanyByIdParams } from '@nxsys/types/api/company';
import { Icons } from '@nxsys/types/icons';
import { ICompany } from '@nxsys/types/models/company';
import { useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PayrollSettingForm, {
  PayrollSettingFormRef,
  PayrollSettingFormValues,
} from './components/PayrollSettingForm';

type Props = {
  data?: ICompany;
  updateData: (params: UpdateCompanyByIdParams) => Promise<void>;
};

const PayrollSettingTab: React.FC<Props> = ({ data, updateData }) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;
  const params = useParams();
  const companyId = params?.companyId ?? null;
  const formRef = useRef<PayrollSettingFormRef>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSubmit = useCallback(
    async (values: PayrollSettingFormValues) => {
      if (!companyId) return;
      setIsUpdating(true);

      try {
        // NX-6284: the single dropdown value ("day-month") decodes back to
        // the two numbers the API still wants; default 1 April if unset.
        const leaveYearStart = fromLeaveYearStart(
          values.holidayLeaveYearStart?.value,
        );
        const params: UpdateCompanyByIdParams = {
          timesheetApprovalNeeded:
            (values.timesheetApprovalNeeded as string) ??
            YesNoNAEnum.NOT_AVAILABLE,
          payrollApproveNeeded:
            (values.payrollApproveNeeded as string) ??
            YesNoNAEnum.NOT_AVAILABLE,
          listEmprApprovers: values.listEmprApprovers
            ? values.listEmprApprovers.map((item) => item.value)
            : [],
          enableHolidayPay: values.enableHolidayPay ?? false,
          payeHolidayPayRate: values.payeHolidayPayRate
            ? Number(values.payeHolidayPayRate)
            : null,
          holidayPayMethod: values.holidayPayMethod?.value ?? null,
          holidayPayCalculationRule:
            values.holidayPayCalculationRule?.value ?? null,
          includeInTimesheetRate: values.includeInTimesheetRate ?? null,
          // NX-6284: Ireland-only leave year start, default 1 April. The
          // column is NOT NULL, so always send numbers when the field shows.
          ...(isIreland
            ? {
                holidayLeaveYearStartDay: leaveYearStart?.day ?? 1,
                holidayLeaveYearStartMonth: leaveYearStart?.month ?? 4,
              }
            : {}),
        };

        await updateData(params);
      } catch (error) {
        console.error(error);
      } finally {
        setIsUpdating(false);
      }
    },
    [companyId, updateData, messages, isIreland],
  );

  return (
    <div className="PayrollSettingTab">
      {data && (
        <>
          <PayrollSettingForm
            formRef={formRef}
            onSubmit={handleSubmit}
            companyId={companyId ?? undefined}
            isIreland={isIreland}
            defaultValues={{
              timesheetApprovalNeeded:
                (data?.timesheetApprovalNeeded as YesNoNAEnum) ?? null,
              listEmprApprovers: data?.listEmprApprovers
                ? data.listEmprApprovers.map((item: any) => ({
                    label: `${item?.forename || ''} ${item?.surname}`,
                    value: item.id,
                  }))
                : null,
              payrollApproveNeeded:
                (data?.payrollApproveNeeded as YesNoNAEnum) ?? null,
              enableHolidayPay: data?.enableHolidayPay ?? false,
              payeHolidayPayRate: data?.payeHolidayPayRate?.toString() ?? '',
              holidayPayMethod:
                holidayPaymentMethodOptions.find(
                  (item) => item.value === data?.holidayPayMethod,
                ) ?? null,
              holidayPayCalculationRule:
                defaultHolidayPayCalcOptions.find(
                  (item) => item.value === data?.holidayPayCalculationRule,
                ) ?? null,
              includeInTimesheetRate: data?.includeInTimesheetRate ?? null,
              // NX-6284: default 1 April when unset (new or pre-migration company).
              holidayLeaveYearStart:
                leaveYearStartDateOptions.find(
                  (item) =>
                    item.value ===
                    toLeaveYearStart(
                      (data as any)?.holidayLeaveYearStartDay ?? 1,
                      (data as any)?.holidayLeaveYearStartMonth ?? 4,
                    ),
                ) ?? null,
            }}
          />
          <div className="flex items-center justify-start gap-3 pt-4">
            <AppButton
              type="primary"
              name={messages['common.save']}
              leftIcon={
                <Icons.IconCheckCircle
                  color={defaultTheme.theme.palette.white}
                />
              }
              onClick={() => formRef.current?.submit()}
              loading={isUpdating}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default PayrollSettingTab;
